import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class CompanyService {
  private headerCompany$ = new BehaviorSubject<number | null>(null);

  // 🔥 moduleName → companyId
  private moduleCompanyMap = new Map<string, number | null>();

  private activeModule$ = new BehaviorSubject<string | null>(null);

  // Subject to trigger company list refresh in header
  private refreshCompanyListSource = new Subject<void>();
  refreshCompanyList$ = this.refreshCompanyListSource.asObservable();

  triggerCompanyListRefresh() {
    this.refreshCompanyListSource.next();
  }

  // ---- FINAL resolved company ----
  effectiveCompany$ = combineLatest([
    this.activeModule$,
    this.headerCompany$
  ]).pipe(
    map(([module]) => {
      if (module && this.moduleCompanyMap.has(module.toLocaleLowerCase())) {
        return this.moduleCompanyMap.get(module.toLocaleLowerCase())!;
      }
      return this.headerCompany$.value;
    }),
    distinctUntilChanged()
  );

  // ---------- setters ----------
  setHeaderCompany(id: number) {
    this.headerCompany$.next(id);
  }

  registerModuleCompany(module: string, companyId: number | null) {
    this.moduleCompanyMap.set(module.toLocaleLowerCase(), companyId);

    if(companyId>0){
      localStorage.setItem('module_gc_id', companyId.toString());
    }else{
      localStorage.setItem('module_gc_id', '0');
    }

    if(companyId == null || companyId == undefined) {
      this.moduleCompanyMap.delete(module.toLocaleLowerCase());
    }
  }

  clearModuleCompany(module: string) {
    this.moduleCompanyMap.delete(module.toLocaleLowerCase());
  }

  setActiveModule(module: string | null) {
    this.activeModule$.next(module?.toLocaleLowerCase() || null);
  }

  getCurrentCompany(): number | null {
    const module = this.activeModule$.value;
    if (module && this.moduleCompanyMap.has(module.toLocaleLowerCase())) {
      return this.moduleCompanyMap.get(module.toLocaleLowerCase())!;
    }
    return this.headerCompany$.value;
  }

  extractCleanPath(): string {
    // 1. Grab the path directly from the browser
    const path = window.location.pathname;

    // 2. Define exclusion list (keywords + dynamic IDs)
    const excludedKeywords = ['list', 'search', 'filter', 'edit', 'new', 'crud', 'details', 'view'];

    const segments = path.split('/')
      .filter(segment => {
        if (!segment) return false;

        // Filter out the specific CRUD/Action keywords
        if (excludedKeywords.includes(segment.toLowerCase())) return false;

        // Filter out numeric IDs or long UUID/hashes
        const isDynamicId = /^\d+$/.test(segment) || /^[a-f0-9-]{20,}$/.test(segment);
        if (isDynamicId) return false;

        return true;
      });

    // 3. Return lowercase joined by '/' (e.g., "master/activity")
    // If the path is empty (like root), default to 'dashboard'
    return segments.join('/').toLowerCase() || 'dashboard';
  }
}
